import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { staffApi, facilityApi, StaffUser, businessUserApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { BusinessUser } from '../api_types';
import { websocketService } from '../services/websocket';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CourtManagement from './CourtManagement';
import NewCourtScheduler from './BusinessCourtReservations';
import InviteCodeGenerator from './invites/InviteCodeGenerator';
import BusinessEvents from './BusinessEvents';
import FacilityMembers from './dashboard/FacilityMembers';
import EmailMarketing from './Marketing';
import Payments from './payments/Payments';
import PublicUse from './PaidPublicUse';
import QuickstartGuide from './dashboard/QuickstartGuide';
import UpcomingEventsDashboard from './dashboard/UpcomingEventsDashboard';
import ClassManagement from './LessonManagement';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import BusinessSettings from './BusinessSettings';
import StaffManagement from './StaffManagement';
import RecentTransactions from './dashboard/RecentTransactions';
import { 
  HomeIcon, 
  EnvelopeIcon,
  CreditCardIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  SparklesIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { UserCircleIcon as UserCircleIconSolid } from '@heroicons/react/24/solid';
import StringingManagement from './StringingManagement';
import Nexus from '../components/Nexus';

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, logout, businessFacilities } = useAuth();
  const queryClient = useQueryClient();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showLogoutTooltip, setShowLogoutTooltip] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    localStorage.getItem('selectedFacilityId')
  );
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(
    localStorage.getItem('selectedBusinessId')
  );

  // Redirect to facility selection if no facility is selected
  useEffect(() => {
    if (!selectedFacilityId && businessFacilities.length > 0) {
      navigate('/business/facility-selection');
    }
  }, [selectedFacilityId, businessFacilities, navigate]);

  // Use React Query for data fetching
  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['businessUser', authUser?.email, selectedFacilityId, selectedBusinessId],
    queryFn: async () => {
      if (!authUser?.email || !selectedFacilityId || !selectedBusinessId) return null;
      const users = await businessUserApi.getBusinessUsers(selectedBusinessId);
      return users.find((user: BusinessUser) => user.email.toLowerCase() === authUser.email.toLowerCase());
    },
    enabled: !!authUser?.email && !!selectedFacilityId && !!selectedBusinessId,
  });

  const { data: facility, isLoading: isLoadingFacility } = useQuery({
    queryKey: ['facility', selectedFacilityId, selectedBusinessId],
    queryFn: async () => {
      if (!selectedFacilityId || !selectedBusinessId) return null;
      return await facilityApi.getFacility(selectedFacilityId, selectedBusinessId);
    },
    enabled: !!selectedFacilityId && !!selectedBusinessId,
  });

  const { data: staffMembers = [], isLoading: isLoadingStaff } = useQuery({
    queryKey: ['staffMembers', selectedBusinessId],
    queryFn: async () => {
      if (!selectedBusinessId) return [];
      return await staffApi.getStaffUsers(selectedBusinessId);
    },
    enabled: !!selectedBusinessId,
  });

  // WebSocket setup
  useEffect(() => {
    if (selectedFacilityId) {
      websocketService.setQueryClient(queryClient);
      websocketService.forceDisconnect();
      websocketService.connect(selectedFacilityId, 'business-bookings');
    }

    return () => {
      websocketService.forceDisconnect();
    };
  }, [selectedFacilityId, queryClient]);

  const handleFacilityChange = (facilityId: string) => {
    const facility = businessFacilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacilityId(facilityId);
      setSelectedBusinessId(facility.business_id);
      localStorage.setItem('selectedFacilityId', facilityId);
      localStorage.setItem('selectedBusinessId', facility.business_id);
      // Force a refetch of all data
      queryClient.invalidateQueries({ queryKey: ['businessUser'] });
      queryClient.invalidateQueries({ queryKey: ['facility'] });
      queryClient.invalidateQueries({ queryKey: ['staffMembers'] });
    }
  };

  const handleRemoveStaff = async (staffId: string) => {
    try {
      await staffApi.deleteStaffUser(staffId, facility?.id || '');
      queryClient.setQueryData(['staffMembers', selectedFacilityId], 
        (old: StaffUser[] = []) => old.filter(staff => staff.id !== staffId)
      );
    } catch (err) {
      console.error('Error removing staff member:', err);
    }
  };

  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/courts')) return 'facility setup';
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/events')) return 'events';
    if (path.includes('/invites')) return 'member invites';
    if (path.includes('/marketing')) return 'marketing';
    if (path.includes('/payments')) return 'payments';
    if (path.includes('/public-use')) return 'public use';
    if (path.includes('/lessons')) return 'lessons and classes';
    if (path.includes('/stringing')) return 'stringing';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/staff-management')) return 'staff management';
    return 'dashboard';
  };

  const handleLogout = async () => {
    try {
      websocketService.forceDisconnect(); // Force disconnect WebSocket before logout
      await logout();
      navigate('/business/login');
      localStorage.removeItem('quickstartDismissed');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  if (isLoadingUser || isLoadingFacility || isLoadingStaff) {
    return <LoadingSpinner size="lg" />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">User not found</div>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Facility Assigned</h2>
          <p className="text-gray-600 mb-4">
            This business user has not yet been assigned to a facility.
          </p>
          <p className="text-gray-600">
            Please contact the administration at{' '}
            <a href="mailto:briantton@gmail.com" className="text-blue-500 hover:text-blue-700">
              briantton@gmail.com
            </a>{' '}
            to amend this.
          </p>
        </div>
      </div>
    );
  }

  const navigation = [
    { name: 'Dashboard', href: '/business/dashboard', icon: HomeIcon },
    { name: 'Facility Setup', href: '/business/dashboard/courts', icon: Cog6ToothIcon },
    { name: 'Schedule', href: '/business/dashboard/schedule', icon: CalendarDaysIcon },
    { name: 'Events', href: '/business/dashboard/events', icon: SparklesIcon },
    { name: 'Lessons and Classes', href: `/business/dashboard/${facility?.id}/lessons`, icon: AcademicCapIcon },
    { name: 'Stringing', href: '/business/dashboard/stringing', icon: WrenchScrewdriverIcon },
    { name: 'Marketing', href: '/business/dashboard/marketing', icon: EnvelopeIcon },
    ...(!facility?.isPPU ? [
      { name: 'Member Invites', href: '/business/dashboard/invites', icon: UserPlusIcon },
    ] : []),
    { name: 'Staff Management', href: '/business/dashboard/staff-management', icon: UserPlusIcon },
    { name: 'Payments', href: '/business/dashboard/payments', icon: CreditCardIcon },
    { name: 'Public Use', href: '/business/dashboard/public-use', icon: UserPlusIcon },
    { name: 'Settings', href: '/business/dashboard/settings', icon: Cog6ToothIcon },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex flex-1">
        {/* Sidebar Navigation */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-white shadow-sm h-screen fixed transition-all duration-300`}>
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 px-4 py-4 border-b">
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && (
                  <h1 className="text-xl font-bold text-gray-900">Business Portal</h1>
                )}
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  {isSidebarCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              </div>
              {!isSidebarCollapsed && (
                <div className="mt-3">
                  <select
                    value={selectedFacilityId || ''}
                    onChange={(e) => handleFacilityChange(e.target.value)}
                    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Facility</option>
                    {businessFacilities
                      .sort((a, b) => {
                        const aName = a.name.toLowerCase();
                        const bName = b.name.toLowerCase();
                        
                        // Split the names into parts (text and numbers)
                        const aParts = aName.match(/(\d+|\D+)/g) || [];
                        const bParts = bName.match(/(\d+|\D+)/g) || [];
                        
                        // Compare each part
                        for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
                          const aPart = aParts[i];
                          const bPart = bParts[i];
                          
                          // If both parts are numbers, compare numerically
                          if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
                            const diff = parseInt(aPart) - parseInt(bPart);
                            if (diff !== 0) return diff;
                          } else {
                            // Otherwise compare as strings
                            const diff = aPart.localeCompare(bPart);
                            if (diff !== 0) return diff;
                          }
                        }
                        
                        // If all parts match up to the length of the shorter name,
                        // the shorter name comes first
                        return aParts.length - bParts.length;
                      })
                      .map((facility) => (
                        <option key={facility.id} value={facility.id}>
                          {facility.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1">
              {navigation.map((nav) => {
                const Icon = nav.icon;
                return (
                  <Link
                    key={nav.name}
                    to={nav.href}
                    className={`${
                      getActiveSection() === nav.name.toLowerCase()
                        ? 'bg-indigo-50 text-indigo-600 border-indigo-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                    } group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-2`}
                  >
                    <Icon className={`${isSidebarCollapsed ? 'mx-auto' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
                    {!isSidebarCollapsed && nav.name}
                  </Link>
                );
              })}
            </nav>
            <div className="flex-shrink-0 px-4 py-4 border-t">
              <div className="relative flex justify-center">
                <button
                  onClick={handleLogout}
                  type="button"
                  className={`$${isSidebarCollapsed ? 'w-12 h-12 min-w-0 p-0 flex items-center justify-center' : 'w-full flex items-center justify-center px-4 py-2'} border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  onMouseEnter={() => setShowLogoutTooltip(true)}
                  onMouseLeave={() => setShowLogoutTooltip(false)}
                  aria-label="Logout"
                >
                  {isSidebarCollapsed ? (
                    <ArrowRightOnRectangleIcon className="h-6 w-6" />
                  ) : (
                    'Logout'
                  )}
                </button>
                {isSidebarCollapsed && showLogoutTooltip && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 bg-gray-900 text-white rounded px-2 py-1 text-xs shadow z-50 whitespace-nowrap">
                    Logout
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          {!selectedFacilityId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900">Please select a facility</h2>
                <button
                  onClick={() => navigate('/business/facility-selection')}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Select Facility
                </button>
              </div>
            </div>
          ) : (
            <main className="flex-1 flex flex-col">
              {getActiveSection() === 'dashboard' && (
                <div className="bg-white shadow rounded-2xl border p-0 divide-y divide-gray-100">
                  <div className="p-8">
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2 mb-8">
                      <HomeIcon className="h-7 w-7 text-indigo-500" />
                      Dashboard
                    </h2>
                    {facility && (
                      <QuickstartGuide 
                        facility={facility} 
                        businessId={selectedBusinessId || ''}
                        onComplete={() => {
                          localStorage.setItem('quickstartDismissed', 'true');
                        }}
                        showSubscriptionFeatures={!facility.isPPU}
                      />
                    )}
                  </div>

                  <div className="p-8">
                    {facility && <UpcomingEventsDashboard 
                      facilityId={facility.id} 
                      businessId={selectedBusinessId || ''}
                    />}
                  </div>

                  <div className="p-8">
                    {facility && <RecentTransactions businessId={selectedBusinessId || ''} facilityId={facility.id} />}
                  </div>
                  <div className="pt-8 pb-0 px-8">

                    <div className="bg-white shadow rounded-2xl p-10 flex flex-col items-center justify-center min-h-[300px]">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                          <UserCircleIconSolid className="h-7 w-7 text-indigo-500" />
                          Staff Members
                        </h2>
                        <div className="mt-4 sm:mt-0">
                          <p className="text-sm text-gray-500">
                            Total Staff Members: {staffMembers.length}
                          </p>
                        </div>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-100 w-full">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {staffMembers.map((staff, idx) => (
                              <tr key={staff.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-indigo-50'}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex items-center gap-3">
                                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs">
                                    {staff.name && staff.name.split(' ').length > 1
                                      ? `${staff.name.split(' ')[0][0] || ''}${staff.name.split(' ')[1][0] || ''}`.toUpperCase()
                                      : `${staff.name?.[0] || ''}`.toUpperCase()}
                                  </span>
                                  {staff.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{staff.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{staff.phone_number}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{staff.role}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                  <button
                                    onClick={() => handleRemoveStaff(staff.id)}
                                    className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 rounded-full px-4 py-1 transition-colors duration-150 font-semibold"
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    {facility && <FacilityMembers facilityId={facility.id} businessId={selectedBusinessId || ''} />}
                  </div>
                </div>
              )}
              {getActiveSection() === 'facility setup' && facility && (
                <CourtManagement 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''} 
                />
              )}
              {getActiveSection() === 'schedule' && facility && (
                <NewCourtScheduler 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''}
                />
              )}
              {getActiveSection() === 'events' && facility && (
                <BusinessEvents 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''}
                />
              )}
              {getActiveSection() === 'lessons and classes' && facility && (
                <ClassManagement 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''}
                />
              )}
              {getActiveSection() === 'member invites' && facility && !facility.isPPU && (
                <InviteCodeGenerator 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''}
                />
              )}
              {getActiveSection() === 'marketing' && facility && (
                <EmailMarketing 
                  facilityId={facility.id} 
                  businessId={selectedBusinessId || ''}
                />
              )}
              {getActiveSection() === 'payments' && facility && (
                <Payments facilityId={facility.id} businessId={selectedBusinessId || ''} />
              )}
              {getActiveSection() === 'public use' && facility && (
                <PublicUse businessId={selectedBusinessId || ''} facilityId={facility.id} />
              )}
              {getActiveSection() === 'settings' && facility && (
                <BusinessSettings />
              )}
              {getActiveSection() === 'stringing' && facility && (
                <div className="bg-white shadow rounded-lg p-6">
                  <StringingManagement 
                    facilityId={facility.id} 
                    businessId={selectedBusinessId || ''}
                  />
                </div>
              )}
              {getActiveSection() === 'staff management' && facility && (
                <StaffManagement facilityId={facility.id} businessId={selectedBusinessId || ''} />
              )}
            </main>
          )}
        </div>
      </div>
      <Nexus businessId={selectedBusinessId || ''} facilityId={selectedFacilityId || ''} />
    </div>
  );
};

export default BusinessDashboard; 